import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IPTableColumnInfo } from '@libs/common-components';
import { CommonAuthService } from '@libs/common-services';
import { isEmpty, isEqual, isUndefined, omitBy, sortBy } from 'lodash-es';
import { InputSwitchChangeEvent } from 'primeng/inputswitch';
import { Listbox } from 'primeng/listbox';
import {
  ICampaignFilterContext,
  TaxonomyDateRange,
  TaxonomyDimension
} from '../../../models/ICampaignFilterContext';
import { ITaxonomyTabFilterConfig } from '../../../models/ITaxonomyTabFilterConfig';
import { TaxonomyFeature } from '../../../models/TaxonomyFeature';
import { TaxonomyTab } from '../../../models/TaxonomyTab';
import { Constants } from '../campaigns-view.constants';

@Component({
  selector: 'lib-taxonomy-campaign-view-filter-context',
  templateUrl: './campaign-view-filter-context.component.html',
  styleUrls: ['./campaign-view-filter-context.component.scss'],
  standalone: false
})
export class CampaignViewFilterContextComponent
  implements OnChanges, AfterViewInit
{
  private static readonly activeTabKey = 'activeTab';

  @ViewChild('columnSelector') columnSelector!: Listbox;
  @ViewChild('columnSelectorButton') columnSelectorButton!: ElementRef;

  @Input() activeTab: TaxonomyTab = TaxonomyTab.unmapped;
  @Input() campaignViewColumns: IPTableColumnInfo[] = [];
  @Input() filterView: TaxonomyFeature = TaxonomyFeature.GA;
  @Input() groupByDimension!: TaxonomyDimension;
  @Output() campaignFilterChange = new EventEmitter<ICampaignFilterContext>();
  @Output() aiRecommendationsToggle = new EventEmitter<boolean>();
  @Output() approveAll = new EventEmitter<void>();

  public TaxonomyTab = TaxonomyTab;
  public dateRangeOptions = Constants.dateRangeOptions;
  public groupByDimensions: { label: string; value: string }[] =
    Constants.groupByDimensions;

  public campaignViewFilterForm!: FormGroup;
  public filterConfig: ITaxonomyTabFilterConfig;
  public accordionFilterPlaceholder = 'Search data source';
  public selectedCampaignViewColumns: IPTableColumnInfo[] = [];
  public showAiRecommendations = false;

  constructor(
    private readonly authService: CommonAuthService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.filterConfig =
      Constants.taxonomyTabConfig[this.filterView][this.activeTab];
    this.initializeFilterContextForm();
  }

  private initializeFilterContextForm() {
    this.groupByDimension = TaxonomyDimension.compositeDataSourceName;
    this.campaignViewFilterForm = new FormGroup({
      groupByDimension: new FormControl<TaxonomyDimension>(
        this.groupByDimension
      ),
      dimensionFilter: new FormControl<string | undefined>(undefined),
      dateRange: new FormControl<TaxonomyDateRange | undefined>(
        TaxonomyDateRange.last30Days
      ),
      tableColumns: new FormControl<string[] | undefined>(undefined),
      showAiRecommendations: new FormControl<boolean>(true)
    });
  }

  @HostListener('document:click', ['$event'])
  listboxClickOut(event: Event) {
    if (
      this.columnSelector &&
      !this.columnSelector.el.nativeElement.contains(event.target) &&
      this.columnSelectorButton.nativeElement !== event.target
    ) {
      // logic to hide your p-listbox
      this.columnSelector.el.nativeElement.style.display = 'none';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes[CampaignViewFilterContextComponent.activeTabKey] &&
      changes[CampaignViewFilterContextComponent.activeTabKey]?.currentValue
    ) {
      const currentActiveTabValue = changes[
        CampaignViewFilterContextComponent.activeTabKey
      ]?.currentValue as TaxonomyTab;

      const previousActiveTabValue = changes[
        CampaignViewFilterContextComponent.activeTabKey
      ]?.previousValue as TaxonomyTab;

      if (previousActiveTabValue !== currentActiveTabValue) {
        this.initializeFilterContextForm();
      }
      this.activeTab = currentActiveTabValue || this.activeTab;
    }

    if (
      !isEqual(
        sortBy(changes['campaignViewColumns']?.currentValue, 'field'),
        sortBy(changes['campaignViewColumns']?.previousValue, 'field')
      )
    ) {
      const tableColumns = changes['campaignViewColumns']?.currentValue
        .map((column: IPTableColumnInfo) => column.field)
        .filter((column: string) => !isEmpty(column));
      this.campaignViewFilterForm.patchValue({
        tableColumns
      });
      this.campaignFilterChange.emit({
        tableColumns,
        groupByDimension:
          changes['groupByDimension']?.currentValue || this.groupByDimension
      } as ICampaignFilterContext);
    }
    if (
      this.filterView === TaxonomyFeature.AI &&
      !isEqual(
        changes['groupByDimension']?.currentValue,
        changes['groupByDimension']?.previousValue
      )
    ) {
      this.campaignViewFilterForm.patchValue({
        groupByDimension: changes['groupByDimension']?.currentValue
      });
    }
    this.filterConfig =
      Constants.taxonomyTabConfig[this.filterView][this.activeTab];
  }

  ngAfterViewInit() {
    this.columnSelector.el.nativeElement.style.display = 'none';
    // Emit initial AI recommendations state
    this.aiRecommendationsToggle.emit(
      this.campaignViewFilterForm.value.showAiRecommendations
    );

    this.setShowAiRecommendationsToggle();
    this.cdr.detectChanges();
  }

  onAiRecommendationsToggle(value: InputSwitchChangeEvent) {
    this.campaignViewFilterForm
      .get('showAiRecommendations')
      ?.setValue(value.checked);
    this.aiRecommendationsToggle.emit(value.checked);
  }

  refreshCampaignRecords(value?: string, fieldName?: string) {
    if (fieldName) {
      this.campaignViewFilterForm.patchValue({
        [fieldName]: value
      });
      this.cdr.detectChanges();
    }

    const campaignFilterContext = this.campaignViewFilterForm.value;
    if (!campaignFilterContext.tableColumns?.length) {
      campaignFilterContext.tableColumns = this.campaignViewColumns
        .filter((column) => !isEmpty(column.header) && !isEmpty(column.field))
        .map((column) => column.field);
    }

    this.setAccordionFilterPlaceholder();
    this.campaignFilterChange.emit(
      omitBy(
        campaignFilterContext as ICampaignFilterContext,
        isUndefined
      ) as ICampaignFilterContext
    );
    this.cdr.detectChanges();
  }

  toggleColumnSelector() {
    const columnSelectorElement = this.columnSelector.el.nativeElement;
    if (columnSelectorElement.style.display === 'none') {
      columnSelectorElement.style.display = 'block';
    } else {
      columnSelectorElement.style.display = 'none';
    }
  }

  private setAccordionFilterPlaceholder() {
    this.accordionFilterPlaceholder = `Search ${
      this.campaignViewFilterForm.value.groupByDimension ===
      'compositeDataSourceName'
        ? 'data source'
        : this.campaignViewFilterForm.value.groupByDimension
    }`;
  }

  private setShowAiRecommendationsToggle() {
    this.showAiRecommendations = this.authService.isInternalUser(
      this.authService.getUser()?.user ?? null
    );
    this.aiRecommendationsToggle.emit(this.showAiRecommendations);
  }

  onApproveAll() {
    this.approveAll.emit();
  }
}
